// useSequence.ts (CX2) - the beat driver for the cinematic table.
//
// Ported verbatim from the POC (apps/web/src/poc/useSequence.ts), now a SHIPPED primitive: the self-paced
// stepper CinematicFelt drives the frame stream with. research/08c's verdict was "no library" - a small hook
// is simpler, directly unit-testable with the repo's vitest, and maps naturally onto the frame stream's
// one-frame-per-action shape (CX0 §2.2).
//
// research/08a: "instant" is the opt-in speed-up, never the default. Even GTO Wizard's Trainer ships a
// Normal/Fast/Turbo control rather than snapping opponents' decisions in instantly. So this autoplays,
// stepped by the action, and is always interruptible (tap to advance, space to pause) - but SELF-PACED by
// default (Joe's call): it starts paused.
import { useCallback, useEffect, useRef, useState } from 'react';

export interface SequenceOptions {
  /** autoplay speed multiplier (higher = faster). */
  speed?: number;
  /**
   * CX4 - a HALT boundary the do-modes drive: the sequence auto-advances up to and including this frame,
   * then stops, and `next()` will not cross it. Undefined = no halt (play to the end - the Observe watch).
   * The do-mode wrappers set it to hero's next decision frame, so the cinematic playback pauses for hero to
   * act, then they raise it (a rebuilt hand) and playback resumes. Position is NEVER reset when it moves, so
   * raising the halt lets a still-`playing` sequence flow straight on from where it rested.
   */
  haltAt?: number;
  /** start playing (the do-modes bring the action TO hero automatically). Default false = self-paced watch. */
  autoplay?: boolean;
  /**
   * CX-POLISH - the frame to START at on mount (default 0). A do-mode surface that UNMOUNTS the felt for an
   * excursion (Diagnose's report / counterfactual / folded-watch) and then returns to the live view remounts
   * this hook fresh; without this it would restart at frame 0 and autoplay the whole hand back to the halt (a
   * visible re-run). Passing the resting frame back in makes the remount pick up where it left off. Only read
   * at mount (a lazy useState initial), so feeding back the live index can never loop.
   */
  startAt?: number;
}

export interface Sequence {
  /** current beat index (0..count-1). */
  i: number;
  playing: boolean;
  atEnd: boolean;
  /** at (or past) the current halt boundary - hero's turn in the do-modes, not the natural end of the hand. */
  atHalt: boolean;
  play: () => void;
  pause: () => void;
  toggle: () => void;
  next: () => void;
  prev: () => void;
  restart: () => void;
  skipToEnd: () => void;
  /** jump straight to a beat (the running-ledger rows are clickable, CX0 §4.2). */
  goTo: (i: number) => void;
}

/**
 * Drive an index from 0..count-1, auto-advancing after `delayFor(i)` milliseconds while playing.
 *
 * Pure in (count, delayFor). The timer is torn down on every index change, so a manual `next()` cancels the
 * pending auto-advance rather than racing it. `delayFor` is held in a ref so callers can pass an inline arrow
 * without re-firing the effect every render.
 */
export function useSequence(
  count: number,
  delayFor: (i: number) => number,
  opts: SequenceOptions = {},
): Sequence {
  const { speed = 1, haltAt, autoplay = false, startAt } = opts;
  // lazy initial: clamp the requested start into range, only read once at mount (see SequenceOptions.startAt).
  const [i, setI] = useState(() => Math.max(0, Math.min(startAt ?? 0, count - 1)));
  // SELF-PACED by default (Joe's call): start paused and read at your own speed with Next/Back or the arrow
  // keys. The do-modes pass autoplay so the action comes TO hero, halting at his decision (haltAt).
  const [playing, setPlaying] = useState(autoplay);
  const atEnd = i >= count - 1;
  // the ceiling this render may advance to: the halt boundary if set (and in range), else the last frame.
  const ceiling = haltAt !== undefined ? Math.min(haltAt, count - 1) : count - 1;
  const atHalt = haltAt !== undefined && i >= ceiling && !atEnd;

  const delayRef = useRef(delayFor);
  delayRef.current = delayFor;

  useEffect(() => {
    // stop auto-advancing at the natural end OR at the halt boundary (hero is on the clock).
    if (!playing || i >= ceiling) return;
    const ms = Math.max(120, delayRef.current(i) / speed);
    const t = setTimeout(() => setI((x) => Math.min(x + 1, ceiling)), ms);
    return () => clearTimeout(t);
  }, [i, playing, ceiling, speed]);

  // clamp the index if the stream shrinks under us (a shorter hand loaded into the same mounted felt).
  useEffect(() => {
    setI((x) => Math.min(x, Math.max(0, count - 1)));
  }, [count]);

  // Manual advance also respects the halt: hero cannot Next past his own decision (the ceiling), but Back
  // and jumps behind it stay free so the action so far can be reviewed.
  const next = useCallback(() => setI((x) => Math.min(x + 1, ceiling)), [ceiling]);
  const prev = useCallback(() => setI((x) => Math.max(x - 1, 0)), []);

  return {
    i,
    playing,
    atEnd,
    atHalt,
    play: useCallback(() => setPlaying(true), []),
    pause: useCallback(() => setPlaying(false), []),
    toggle: useCallback(() => setPlaying((p) => !p), []),
    next,
    prev,
    restart: useCallback(() => {
      setI(0);
      setPlaying(true);
    }, []),
    skipToEnd: useCallback(() => setI(count - 1), [count]),
    goTo: useCallback((to: number) => setI(() => Math.max(0, Math.min(to, count - 1))), [count]),
  };
}
